import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { signIn, setupTokenRefresh } from '@/services/tokenExchange';

import {
  CognitoPasswordChangeProps,
  ConfirmForgotPasswordProps,
  ConfirmVerificationCodeProps,
  ForgotPasswordProps,
  InitialLoginProps,
  LoginStep,
  LoginSteps,
  LoginValues,
  LoginStepProps,
  ClassCodeUserValues,
  SessionSetupParams,
} from '@/types/login';
import { cognitoService } from '@/services/cognito';
import { authActions } from '@/store/reducers/auth';
import commonUtils from '@/utils/common';
import InitialLogin from '@/components/LoginForm/InitialLogin';
import VerifyRegistrationCode from '@/components/LoginForm/VerifyRegistrationCode';
import ForgotPassword from '@/components/LoginForm/ForgotPassword';
import ConfirmForgotPassword from '@/components/LoginForm/ConfirmForgotPassword';
import CognitoPasswordChange from '@/components/LoginForm/CognitoPasswordChange';
import ClassCode from '@/components/LoginForm/ClassCode';
import AvatarUpdate from '@/components/LoginForm/AvatarUpdate';
import ClassCodeRegistration from '@/components/LoginForm/ClassCodeRegistration';
import { CognitoUser, CognitoUserAttribute } from 'amazon-cognito-identity-js';
import { authService, userService } from '@/services';
import { jwtDecode } from 'jwt-decode';
import { FormikHelpers } from 'formik';
import { registerService } from '@/services/register';
import config from '@/config';
import ClassCodeError from '@/components/LoginForm/ClassCodeError';
import { CognitoSignUpValidation } from '@/types/user';
import { isWafBlockedError } from '@/utils/wafBlockedError';

export interface UseLoginInterface {
  isFirstTime: boolean;
  loading: boolean;
  loginStep: LoginStep;
  LoginComponent: React.FC<LoginStepProps | InitialLoginProps> | undefined;
  stepProps: () =>
    | {}
    | ForgotPasswordProps
    | ConfirmForgotPasswordProps
    | InitialLoginProps
    | ConfirmVerificationCodeProps
    | CognitoPasswordChangeProps;
  currentLoginStep: LoginSteps | undefined;
  restartLogin: () => void;
  setLoginStep: React.Dispatch<React.SetStateAction<LoginStep>>;
}

const UseLogin = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [cognitoUser, setCognitoUser] = useState<CognitoUser | null>(null);
  const [cognitoUserAttributes, setCognitoUserAttributes] = useState<CognitoUserAttribute[]>([]);
  const [loginDetails, setLoginDetails] = useState<LoginValues>({});
  const [loginStep, setLoginStep] = useState(
    config.isMGWebsite ? LoginStep.MG_LOGIN : LoginStep.LOGIN
  );
  const [loginError, setLoginError] = useState<string | null>(null);
  const [passwordRequested, setPasswordRequested] = useState<boolean>(false);
  const [classCodeState, setClassCodeState] = useState<string | null>(null);
  const [classCodeErrorObject, setClassCodeErrorObject] = useState<any>({
    title: '',
    message: '',
  });
  const [showDealLimitReachedModal, setShowDealLimitReachedModal] = useState<boolean>(false);

  const onCloseDealLimitReachedModal = useCallback(() => {
    setShowDealLimitReachedModal(false);
  }, []);

  const loginSteps: LoginSteps[] = [
    {
      type: LoginStep.LOGIN,
      title: 'signIn',
      subTitle: 'doYouHaveAClassCode',
      component: InitialLogin,
    },
    {
      type: LoginStep.MG_LOGIN,
      title: 'mgWebsite.loginFormTitle',
      subTitle: 'mgWebsite.loginFormSubTitle',
      component: InitialLogin,
    },
    {
      type: LoginStep.CONFIRM_VERIFICATION_CODE,
      title: 'signIn',
      subTitle: 'enterVerificationCode',
      component: VerifyRegistrationCode,
    },
    {
      type: LoginStep.FORGOT_PASSWORD,
      title: 'forgotPassword',
      subTitle: 'pleaseEnterYourDetails',
      component: ForgotPassword,
    },
    {
      type: LoginStep.CONFIRM_FORGOT_PASSWORD,
      title: 'forgotPassword',
      subTitle: 'enterVerificationCodeAndNewPassword',
      component: ConfirmForgotPassword,
    },
    {
      type: LoginStep.COGNITO_PASSWORD_CHANGE,
      component: CognitoPasswordChange,
    },
    {
      type: LoginStep.CLASS_CODE,
      title: 'appName',
      subTitle: 'provideClassCode',
      component: ClassCode,
    },
    {
      type: LoginStep.CLASS_CODE_REGISTRATION,
      title: 'appName',
      subTitle: 'pleaseEnterYourDetails',
      component: ClassCodeRegistration,
    },
    {
      type: LoginStep.CLASS_CODE_ERROR,
      component: ClassCodeError,
    },
    {
      type: LoginStep.AVATAR_UPDATE,
      component: AvatarUpdate,
    },
  ];

  const LoginComponent = useMemo(
    () => loginSteps.find(step => step.type === loginStep)?.component,
    [loginStep]
  );

  const currentLoginStep = useMemo(
    () => loginSteps.find(step => step.type === loginStep),
    [loginStep]
  );
  //TODO: Need to fix, because setIsFirstTime is not used here
  //eslint-disable-next-line
  const [isFirstTime, setIsFirstTime] = useState(true);

  const OnLoginSubmitted = async (values: { email: string; password: string }) => {
    setLoading(true);
    setLoginDetails(values);

    try {
      //DEMO HOTFIX
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const isClassCode = !emailRegex.test(values.email);

      const userCognitoInfo = await cognitoService.authenticate(
        values.email,
        values.password,
        isClassCode
      );

      if (userCognitoInfo.newPasswordRequired && userCognitoInfo.cognitoUser) {
        setCognitoUser(userCognitoInfo.cognitoUser);
        setCognitoUserAttributes(userCognitoInfo.requiredAttributes ?? []);
        setLoginStep(LoginStep.COGNITO_PASSWORD_CHANGE);
        setLoading(false);
        return;
      }

      if (!userCognitoInfo.token || !userCognitoInfo.userId) {
        setLoading(false);
        return;
      }

      const nativeLanguage = userCognitoInfo.nativeLanguage?.Value ?? 'pt';
      const role = userCognitoInfo.role?.Value ?? '';

      // Set native language and role
      localStorage.setItem('userEmail', values.email);
      dispatch(authActions.setNativeLanguage(nativeLanguage));
      if (role) {
        dispatch(authActions.setCognitoRole(role));
      }
      signIn(dispatch, userCognitoInfo.token, userCognitoInfo.userId);
      setLoading(false);
    } catch (e: unknown) {
      const errCode = e as string;

      const errMsg = errCode === 'NotAuthorizedException' ? 'invalidUsernameOrPassword' : errCode;

      if (errCode === 'UserNotConfirmedException') {
        try {
          await cognitoService.resendVerificationCode(values.email);
        } catch (err: unknown) {
          commonUtils.showToast(t(err as string));
          setLoading(false);
          return;
        }

        setLoginStep(LoginStep.CONFIRM_VERIFICATION_CODE);
        commonUtils.showToast(t('verificationCodeResentCheckEmail'));
        setLoading(false);
        return;
      }

      commonUtils.showToast(t(errMsg));
      setLoading(false);
      return;
    }
  };

  const OnResendForgotPassword = async () => {
    if (!loginDetails.email) {
      return;
    }

    setLoading(true);

    try {
      await cognitoService.forgotPassword(loginDetails.email);
    } catch (e: any) {
      commonUtils.showToast(t(e.code as string));

      return;
    } finally {
      setLoading(false);
    }
  };

  const OnCompleteForgotPassword = async () => {
    setPasswordRequested(false);
    setLoginStep(LoginStep.LOGIN);
  };

  const OnSubmitVerificationForgotPassword = async (values: {
    code: string;
    password: string;
    confirmPassword: string;
  }) => {
    if (!loginDetails.email) {
      return;
    }

    setLoading(true);

    try {
      await cognitoService.confirmForgotPassword(
        loginDetails.email
          .replace(/\s/g, '')
          .replace(/\u202B|\u202C/g, '')
          .split('')
          .join(''),
        values.code,
        values.password
      );
      setLoginStep(LoginStep.LOGIN);

      commonUtils.showToast(t('password_changed_successfully'), {
        autoClose: 2000,
      });
    } catch (e: any) {
      commonUtils.showToast(t(e.code as string));

      return;
    } finally {
      setLoading(false);
    }
  };

  const OnSubmitForgotPassword = async (
    values: { email: string },
    formikHelpers: FormikHelpers<{ email: string | null }>
  ) => {
    setLoginDetails(values);

    setLoading(true);

    const isEmail = commonUtils.isEmail(values.email);

    if (isEmail) {
      try {
        await cognitoService.forgotPassword(
          values.email
            .replace(/\s/g, '')
            .replace(/\u202B|\u202C/g, '')
            .split('')
            .join('')
        );
        setLoginStep(LoginStep.CONFIRM_FORGOT_PASSWORD);
      } catch (e: any) {
        commonUtils.showToast(t(e.code as string));
        return;
      } finally {
        setLoading(false);
      }
    } else {
      try {
        const response = await userService.requestPasswordChange(values.email);
        if (response) {
          setPasswordRequested(true);
        } else {
          const errorMessage = t('invalidEmailOrUsername', { username: values.email }) ?? '';
          formikHelpers.setFieldError('email', errorMessage);
        }
      } catch (e: any) {
        // A WAF block is handled by the axios interceptor (redirect to
        // /waf-blocked or the WAF popup). Bail out so we don't briefly flash
        // "invalid username" for a username that is actually valid.
        if (isWafBlockedError(e)) {
          return;
        }
        const errorMessage = t('invalidUsernameinForgotPassword', { username: values.email }) ?? '';
        formikHelpers.setFieldError('email', errorMessage);
        return;
      } finally {
        setLoading(false);
      }
    }
  };

  const OnVerifyAccountSubmitted = async (values: { code: string }) => {
    if (!loginDetails.email || !loginDetails.password) {
      return;
    }

    setLoading(true);

    try {
      await cognitoService.verifyAccount(loginDetails.email, values.code);
    } catch (e: any) {
      commonUtils.showToast(t(e.code as string));
      return;
    }

    try {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const isClassCode = !emailRegex.test(loginDetails.email);

      const userCognitoInfo = await cognitoService.authenticate(
        loginDetails.email,
        loginDetails.password,
        isClassCode
      );

      if (userCognitoInfo.newPasswordRequired && userCognitoInfo.cognitoUser) {
        setCognitoUser(userCognitoInfo.cognitoUser);
        setCognitoUserAttributes(userCognitoInfo.requiredAttributes ?? []);
        setLoginStep(LoginStep.COGNITO_PASSWORD_CHANGE);
        return;
      }

      if (!userCognitoInfo.token || !userCognitoInfo.userId) {
        commonUtils.showToast(t('accountVerifiedCanLoginNow'));
        setLoginStep(LoginStep.LOGIN);
        return;
      }

      const nativeLanguage = userCognitoInfo.nativeLanguage?.Value ?? 'pt';
      const role = userCognitoInfo.role?.Value ?? '';

      localStorage.setItem('userEmail', loginDetails.email);
      dispatch(authActions.setNativeLanguage(nativeLanguage));
      if (role) {
        dispatch(authActions.setCognitoRole(role));
      }
      signIn(dispatch, userCognitoInfo.token, userCognitoInfo.userId);
      commonUtils.showToast(t('accountVerifiedSigningIn'));
    } catch (e: unknown) {
      const errCode = e as string;
      const errMsg = errCode === 'NotAuthorizedException' ? 'invalidUsernameOrPassword' : errCode;
      commonUtils.showToast(t(errMsg));
      setLoginStep(LoginStep.LOGIN);
    } finally {
      setLoading(false);
    }
  };

  const OnSubmitCognitoPasswordChange = async (values: {
    password: string;
    confirmPassword: string;
  }) => {
    const { password, confirmPassword } = values;

    if (
      !loginDetails.email ||
      !loginDetails.password ||
      !password ||
      !confirmPassword ||
      password !== confirmPassword
    ) {
      return;
    }

    setLoading(true);

    try {
      const userCognitoInfo = await cognitoService.forceChangeNewPassword(
        cognitoUser as CognitoUser,
        cognitoUserAttributes,
        password
      );

      if (!userCognitoInfo.token || !userCognitoInfo.userId) {
        return;
      }

      commonUtils.showToast(t('password_changed_successfully'), {
        autoClose: 2000,
      });

      const nativeLanguage = userCognitoInfo.nativeLanguage?.Value ?? 'pt';
      const role = userCognitoInfo.role?.Value ?? '';

      setLoginDetails({
        email: loginDetails.email,
        language: nativeLanguage,
        token: userCognitoInfo.token,
        userId: userCognitoInfo.userId,
      });

      if (role) {
        dispatch(authActions.setCognitoRole(role));
      }

      const isEmail = commonUtils.isEmail(loginDetails.email);
      const isTeacher = role?.toLowerCase() === 'teacher';

      //Prevent showing avatar step for students with usernames or teachers
      if (isEmail && !isTeacher) {
        setLoginStep(LoginStep.AVATAR_UPDATE);
      } else {
        localStorage.setItem('userEmail', loginDetails?.email ?? '');
        dispatch(
          authActions.setNativeLanguage(
            userCognitoInfo?.nativeLanguage?.Value || loginDetails.language || ''
          )
        );
        signIn(dispatch, userCognitoInfo.token, userCognitoInfo.userId);
      }
    } catch (e: any) {
      if (
        (e.name === 'NotAuthorizedException' || e.code === 'NotAuthorizedException') &&
        e.message?.includes('session is expired')
      ) {
        restartLogin();
        commonUtils.showToast(t('cognitoErrors.NewPasswordRequiredSessionExpired'));
      } else {
        commonUtils.showToast(t(e.code as string));
        throw e;
      }
    } finally {
      setLoading(false);
    }
  };

  const OnSubmitAvatarUpdate = async (values: { avatarName: string; avatarColor: string }) => {
    const { avatarName, avatarColor } = values;
    setLoading(true);

    try {
      if (loginDetails.token && loginDetails.userId) {
        // Set native language
        localStorage.setItem('userEmail', loginDetails?.email ?? '');
        dispatch(authActions.setNativeLanguage(loginDetails.language ?? ''));
        signIn(dispatch, loginDetails.token, loginDetails.userId);

        const user = await authService.me(loginDetails.userId);

        if (user?.data && avatarName !== '' && avatarColor !== '') {
          const response = await userService.updateUserAvatar(
            user?.data.data,
            avatarName,
            avatarColor
          );
          if (response) {
            dispatch(
              authActions.setAvatar({
                avatarName,
                avatarColor,
              })
            );
          }
        }
      }
    } catch (e: any) {
      commonUtils.showToast(t(e.code as string));

      return;
    } finally {
      setLoading(false);
    }
  };

  const OnSubmitClassCode = async (
    values: { code: string | null },
    formikHelpers: FormikHelpers<{ code: string | null }>
  ) => {
    setLoading(true);

    const { code } = values;

    if (code)
      try {
        const response = await authService.isClassCodeValid(code);
        setClassCodeState(code);
        setLoading(false);
        setLoginStep(LoginStep.CLASS_CODE_REGISTRATION);
      } catch (e: any) {
        if (isWafBlockedError(e)) {
          return;
        }
        const errorMessage = e?.response?.data?.message;
        let translationKey = 'classCodeDealInvalidCode';

        switch (errorMessage) {
          case 'classCode not active':
            setLoginStep(LoginStep.CLASS_CODE_ERROR);
            setClassCodeErrorObject({
              title: 'classCodeDealNotActive',
              message: 'contactAdmin',
            });
            translationKey = 'classCodeNotActive';
            break;
          case 'deal limit reached':
            setLoginStep(LoginStep.CLASS_CODE_ERROR);
            setClassCodeErrorObject({
              title: 'classCodeDealLimitReached',
              message: 'contactAdmin',
            });
            translationKey = 'classCodeDealLimitReached';
            break;
          case 'deal not valid':
            translationKey = 'classCodeDealNotValid';
            break;
          case 'class limit reached':
            setLoginStep(LoginStep.CLASS_CODE_ERROR);
            setClassCodeErrorObject({
              title: 'classCodeClassLimitReached',
              message: 'contactAdmin',
            });
            translationKey = 'classCodeClassLimitReached';
            break;
        }

        formikHelpers.setFieldError('code', translationKey);
        return;
      } finally {
        setLoading(false);
      }
  };

  const OnCognitoRegistration = async (
    values: ClassCodeUserValues,
    formikHelpers: FormikHelpers<ClassCodeUserValues>
  ) => {
    setLoading(true);

    try {
      const { firstName, lastName, username, password } = values;

      //DEMO HOTFIX
      const email = username + '@melingo.com';
      const nativeLanguage = 'es';
      const role = 'student';
      const classCode = (classCodeState || '').trim();

      const validationData: CognitoSignUpValidation = {
        type: 'classcode',
        classCode: classCode,
      };

      const userId = await createUserAccount(
        email,
        username,
        password,
        nativeLanguage,
        role,
        validationData,
        formikHelpers
      );

      if (!userId) return;

      const authResult = await authenticateUser(username, password);
      if (!authResult || !authResult.userId) return;

      await setupUserSession({
        firstName,
        lastName,
        username,
        userId: authResult.userId,
        classCode,
        authResult,
      });
    } catch (error) {
      console.error('Registration process failed:', error);
      formikHelpers.setFieldError('username', 'anErrorOccurred');
    } finally {
      setLoading(false);
    }
  };

  const getCognitoErrorTranslation = (error: any): string => {
    const errorCode = error?.code || error?.name || error?.__type;
    const errorMessage = typeof error === 'string' ? error : error?.message || '';

    if (
      errorCode === 'UsernameExistsException' ||
      errorMessage.toLowerCase().includes('user already exists')
    ) {
      return 'usernameAlreadyExists';
    }

    if (errorCode) {
      const translationKey = `cognitoErrors.${errorCode}`;
      return t(translationKey) !== translationKey ? translationKey : 'anErrorOccurred';
    }

    return 'anErrorOccurred';
  };

  async function createUserAccount(
    email: string,
    username: string,
    password: string,
    nativeLanguage: string,
    role: string,
    validationData: CognitoSignUpValidation,
    formikHelpers: FormikHelpers<ClassCodeUserValues>
  ): Promise<string | null> {
    try {
      const response = await cognitoService.createClassCodeAccount(
        email,
        username,
        password,
        nativeLanguage,
        role,
        validationData
      );

      if (!response) {
        formikHelpers.setFieldError('username', 'username_invalid');
        return null;
      }

      return response;
    } catch (error: any) {
      const translationKey = getCognitoErrorTranslation(error);
      commonUtils.showToast(t(translationKey));
      formikHelpers.setFieldError(
        'username',
        translationKey === 'usernameAlreadyExists' ? 'usernameAlreadyExists' : 'anErrorOccurred'
      );
      return null;
    }
  }

  async function authenticateUser(username: string, password: string): Promise<any | null> {
    try {
      return await cognitoService.authenticate(username, password, true);
    } catch (error) {
      console.error('Authentication failed after signup:', error);
      return null;
    }
  }

  async function setupUserSession({
    firstName,
    lastName,
    username,
    userId,
    classCode,
    authResult,
  }: SessionSetupParams): Promise<void> {
    const token = authResult.token ?? '';
    localStorage.setItem('token', token);
    localStorage.setItem('userId', userId);

    const userDetails = {
      firstName,
      lastName,
      username,
      classCode,
      userId,
    };

    let isAllowedToRegister: boolean = false;

    const createClassCodeStudent = await registerService.createClassCodeStudent(userDetails);
    isAllowedToRegister = createClassCodeStudent?.data.status !== 'error';

    if (!isAllowedToRegister) {
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      setShowDealLimitReachedModal(true);
      setLoginStep(LoginStep.LOGIN);
      setClassCodeState(null);
      return;
    }

    if (createClassCodeStudent && isAllowedToRegister) {
      const nativeLanguage = authResult.nativeLanguage?.Value ?? 'pt';
      setLoginDetails({
        email: loginDetails.email,
        language: nativeLanguage,
        token: authResult?.token || '',
        userId: authResult?.userId || '',
      });
      setLoginStep(LoginStep.AVATAR_UPDATE);
      setClassCodeState(null);
    }

    if (token) {
      try {
        const decoded = jwtDecode(token);
        setupTokenRefresh(dispatch, decoded.exp);
      } catch (error) {
        console.error('Failed to set up token refresh:', error);
      }
    }
  }

  type StepPropsType =
    | {}
    | ForgotPasswordProps
    | ConfirmForgotPasswordProps
    | InitialLoginProps
    | ConfirmVerificationCodeProps
    | CognitoPasswordChangeProps;

  const stepProps = (): StepPropsType => {
    const propsMap: Partial<Record<LoginStep, StepPropsType>> = {
      [LoginStep.LOGIN]: {
        emitSubmit: OnLoginSubmitted,
        loading,
        goToResetPassword: () => setLoginStep(LoginStep.FORGOT_PASSWORD),
        setLoginError,
      },
      [LoginStep.MG_LOGIN]: {
        emitSubmit: OnLoginSubmitted,
        loading,
        setLoginError,
      },
      [LoginStep.CONFIRM_VERIFICATION_CODE]: {
        emitSubmit: OnVerifyAccountSubmitted,
        loading,
      },
      [LoginStep.FORGOT_PASSWORD]: {
        emitSubmit: OnSubmitForgotPassword,
        loading,
      },
      [LoginStep.CONFIRM_FORGOT_PASSWORD]: {
        emitSubmit: OnSubmitVerificationForgotPassword,
        resendForgotPassword: OnResendForgotPassword,
        loading,
      },
      [LoginStep.COGNITO_PASSWORD_CHANGE]: {
        emitSubmit: OnSubmitCognitoPasswordChange,
        loading,
      },
      [LoginStep.AVATAR_UPDATE]: {
        emitSubmit: OnSubmitAvatarUpdate,
        loading,
      },
      [LoginStep.CLASS_CODE]: {
        emitSubmit: OnSubmitClassCode,
        loading,
      },
      [LoginStep.CLASS_CODE_REGISTRATION]: {
        emitSubmit: OnCognitoRegistration,
        loading,
        setLoginError,
      },
      [LoginStep.LOGIN_ERROR]: {},
    };

    return propsMap[loginStep] ?? {};
  };

  const restartLogin = () => {
    if (loginStep === LoginStep.AVATAR_UPDATE) {
      dispatch(authActions.setToken(''));
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      localStorage.removeItem('loggedUserId');
    }
    setLoginStep(config.isMGWebsite ? LoginStep.MG_LOGIN : LoginStep.LOGIN);
  };

  return {
    isFirstTime,
    loading,
    setLoading,
    loginStep,
    LoginComponent,
    stepProps,
    currentLoginStep,
    restartLogin,
    loginError,
    setLoginError,
    setLoginStep,
    passwordRequested,
    setPasswordRequested,
    OnCompleteForgotPassword,
    showDealLimitReachedModal,
    onCloseDealLimitReachedModal,
    classCodeErrorObject,
    setClassCodeErrorObject,
  };
};

export default UseLogin;
